/* eslint-disable react-hooks/exhaustive-deps */
import { ordersFilterArray } from 'common/enums';
import { formatDateTime, MISSING } from 'common/helpers/format';
import StatusChip from 'components/statusChip';
import Value from 'components/value';
import { useActions } from 'common/hooks/useActions';
import * as T from 'common/types';
import AppTable from 'components/appTable';
import Empty from 'components/empty';
import RowAction from 'components/rowAction';
import Segmented from 'components/segmented';
import Thumb from 'components/thumb';
import Toolbar, { ToolbarRight } from 'components/toolbar';
import SearchBox from 'components/form/searchBox';
import PanelBoxLayout from 'components/layout/panelBox';
import PaginationApp from 'components/pagination';
import { useEffect, useState } from 'react';
import { CALL_THE_WAITER, WaiterIcon } from '../callTheWaiter';
import * as S from './styles';

interface IProps {
  menuList: { list: T.Pages.CafeteriaListItem[]; totalPages: number };
  loading: boolean;
}

/** The segmented filter, with the "everything" option the enum does not carry. */
const STATUS_OPTIONS = [{ id: '', title: 'All' }, ...ordersFilterArray];

export default function CafeteriaOrdersTable({ menuList, loading }: IProps) {
  const { ordersListRequestAction } = useActions();
  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    keyword: '',
    status: ''
  });
  const fetchData = () => {
    ordersListRequestAction(meta);
  };
  useEffect(() => {
    fetchData();
  }, [meta]);
  const handleChangePage = (page: number) => {
    setMeta({ ...meta, page });
  };
  const handleChangeTake = (page_size: number) => {
    setMeta({ ...meta, page_size });
  };
  const handleFilter = (field: string, val: string | number) => {
    setMeta({ ...meta, page: 1, [field]: val });
  };

  function getFoodTitles(items: any) {
    if (!Array.isArray(items)) {
      return [];
    }
    return items.map((item) => item.food?.title).filter((title) => title);
  }

  return (
    <S.Content>
      <PanelBoxLayout className="flush" contentStyle={{ padding: 0 }}>
        <Toolbar>
          <SearchBox onChange={(e) => handleFilter('keyword', e)} placeholder="Search orders" value={meta.keyword} />
          <ToolbarRight>
            <Segmented options={STATUS_OPTIONS} value={meta.status} onChange={(id) => handleFilter('status', id)} />
          </ToolbarRight>
        </Toolbar>
        <AppTable
          headers={[
            /*
             * Orders is the payload column and carries no width, so it takes
             * whatever the others leave. The rest are sized to what they
             * actually hold — a display name, a floor number, a timestamp, a
             * status label — rather than to a share of the table, which is
             * what left the one column with real content in it the narrowest
             * on the screen.
             */
            { id: 1, key: 'orders', title: 'Orders', dir: 'left' },
            { id: 2, key: 'user', title: 'User', dir: 'left', width: 130 },
            { id: 3, key: 'floor', title: 'Floor', dir: 'left', width: 76 },
            { id: 4, key: 'teaboy', title: 'Teaboy', dir: 'left', width: 130 },
            { id: 5, key: 'timeAndDate', title: 'Time and Date', dir: 'left', width: 156 },
            /*
             * 172, not the 150 this column was given in Step 4. That number
             * was reasoned, not measured, and it was short: `Cancelled by tea
             * boy` — the longest label the order map can produce — needs 138px
             * of chip, so the cell needs 138 + 32 of padding. At 150 every
             * tea-boy cancellation on the screen clipped mid-word.
             */
            { id: 6, key: 'status', title: 'Status', dir: 'left', width: 172 },
            { id: 7, key: 'detail', title: '', type: 'actions', width: 64 }
          ]}
          style={{ minWidth: 'auto' }}
          data={menuList?.list}
          loading={loading}
          empty={
            <Empty
              title={
                meta.keyword
                  ? `No orders match “${meta.keyword}”`
                  : meta.status
                    ? 'No orders with that status'
                    : 'No orders yet'
              }
              text={
                meta.keyword || meta.status
                  ? 'Try a different term, or clear the filters to see every order.'
                  : 'Orders placed from the employee app appear here as they arrive.'
              }
            />
          }
          altanfeethiFields={{
            /*
             * Thumb plus the food titles.
             *
             * This cell used to join the titles in JS, cut the result at 100
             * characters and glue on an ellipsis — the same hundred whether
             * the column was 300px or 700px, and no way to read the rest.
             * AppTable already clips what does not fit; the full list goes on
             * the wrapper's title so it is one hover away at any width.
             *
             * The picture was a bare <img> in a 40px circle, and every one of
             * them 404s (see FINDINGS), so the column was a stack of broken
             * image glyphs. Thumb holds the space and falls back to the
             * item's initial.
             */
            orders: (item: T.Pages.OrdersList) => {
              const isCall = item?.cart == null;
              const titles = getFoodTitles(item?.cart?.items);
              const label = isCall ? CALL_THE_WAITER : titles.join(', ');

              return (
                <S.OrderCell className="text-left">
                  <Thumb
                    src={isCall ? null : (item as any)?.image}
                    name={isCall ? CALL_THE_WAITER : titles[0]}
                    icon={isCall ? <WaiterIcon /> : undefined}
                  />
                  <S.OrderTitles title={label || undefined}>
                    <Value fallback={MISSING.none}>{label}</Value>
                  </S.OrderTitles>
                </S.OrderCell>
              );
            },
            user: (item: T.Pages.OrdersList) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.provided}>{item?.user?.displayName}</Value>
              </S.Div>
            ),
            floor: (item: T.Pages.OrdersList) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.none}>{item?.floor?.name}</Value>
              </S.Div>
            ),
            teaboy: (item: T.Pages.OrdersList) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.assigned}>{item?.teaBoy?.user?.displayName}</Value>
              </S.Div>
            ),
            timeAndDate: (item: T.Pages.OrdersList) => (
              <S.Div className="text-left">
                <Value mono>{formatDateTime(item?.localDate)}</Value>
              </S.Div>
            ),
            status: (item: T.Pages.OrdersList) => (
              <S.Div className="text-left">
                <StatusChip domain="order" value={item?.status} fallback={<Value />} />
              </S.Div>
            ),
            /*
             * The design's row action. It was a div with an onClick calling
             * navigate(), which cannot be middle-clicked, opened in a new tab
             * or read by a screen reader. RowAction renders a real anchor with
             * a real href, and the same eye the ticket list uses for the same
             * act.
             */
            detail: (item: T.Pages.OrdersList) => (
              <RowAction to={`/panel/cafeteria/orders/${item?.id}`} label="Open order">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </RowAction>
            )
          }}
        />
      </PanelBoxLayout>
      {menuList?.totalPages ? (
        <PaginationApp
          page={meta.page}
          count={meta.page_size * menuList.totalPages}
          handleChangePage={(page: number) => handleChangePage(page)}
          key={menuList.totalPages}
          pageSize={meta.page_size}
          handleChangeTake={handleChangeTake}
        />
      ) : (
        <></>
      )}
    </S.Content>
  );
}
